import { Sheet } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import type { EvidenceEvent, ExcelSourceRef } from "../../../types/evidence";
import { openDopplerUri } from "../../../utils/dopplerUri";
import "./EvidenceCards.css";

interface ExcelChangeCardProps {
	event: EvidenceEvent;
	/** Required when the ref has an ``artifact_id`` (modified workbook). */
	chatId?: string | null;
}

interface DecoratedExcelRef {
	key: string;
	ref: ExcelSourceRef;
	cellLabel: string;
	href: string | null;
	beforeAfterVisible: boolean;
	beforeText: string;
	afterText: string;
}

interface ExcelOpDetails {
	recalcStatus: string | null;
	warningCount: number;
}

const formatValue = (value: unknown): string => {
	if (value === null || value === undefined) return "—";
	if (typeof value === "object") return JSON.stringify(value);
	return String(value);
};

const cellLabel = (ref: ExcelSourceRef): string => {
	const target = ref.cell ?? ref.range ?? "";
	return target ? `${ref.sheet}!${target}` : ref.sheet;
};

const buildExcelHref = (ref: ExcelSourceRef): string | null => {
	const params = new URLSearchParams({ sheet: ref.sheet });
	// Carry the exact cell/range so the viewer scrolls to and highlights
	// it (SpreadsheetViewer prefers range over cell). Sheet-only refs stay
	// sheet-level. Keep in sync with the backend `_excel_href` deep link.
	if (ref.cell) {
		params.set("cell", ref.cell);
	} else if (ref.range) {
		params.set("range", ref.range);
	}
	// Carry the display filename so the viewer header names the workbook.
	if (ref.filename) {
		params.set("file", ref.filename);
	}
	// Prefer the modified-workbook artifact: that's the file that
	// actually contains the cited change. Fall back to the source
	// workbook only when no artifact id is present.
	if (ref.artifact_id) {
		return `doppler://excel/artifact/${ref.artifact_id}?${params.toString()}`;
	}
	if (ref.file_id != null) {
		return `doppler://excel/${ref.file_id}?${params.toString()}`;
	}
	return null;
};

const decorateExcelRefs = (event: EvidenceEvent): DecoratedExcelRef[] =>
	event.source_refs
		.filter((r): r is ExcelSourceRef => r.kind === "excel")
		.map((ref, index) => ({
			key: `${ref.filename}-${cellLabel(ref)}-${index}`,
			ref,
			cellLabel: cellLabel(ref),
			href: buildExcelHref(ref),
			beforeAfterVisible: ref.before !== undefined || ref.after !== undefined,
			beforeText: formatValue(ref.before),
			afterText: formatValue(ref.after),
		}));

const extractOpDetails = (event: EvidenceEvent): ExcelOpDetails => {
	const details = (event.operation?.details ?? {}) as Record<string, unknown>;
	const recalcStatus =
		typeof details.recalc_status === "string"
			? (details.recalc_status as string)
			: null;
	const warnings = Array.isArray(details.warnings)
		? (details.warnings as unknown[])
		: [];
	return { recalcStatus, warningCount: warnings.length };
};

const ExcelChangeCard: React.FC<ExcelChangeCardProps> = ({ event, chatId }) => {
	const decorated = useMemo(() => decorateExcelRefs(event), [event]);
	const { recalcStatus, warningCount } = useMemo(
		() => extractOpDetails(event),
		[event],
	);
	const footerVisible = recalcStatus !== null || warningCount > 0;

	const handleOpen = useCallback(
		(href: string) => (e: React.MouseEvent) => {
			e.preventDefault();
			void openDopplerUri(href, { chatId });
		},
		[chatId],
	);

	return (
		<article className="evidence-card">
			<header className="evidence-card-header">
				<Sheet className="evidence-card-header-icon" aria-hidden />
				<div className="evidence-card-header-body">
					<h3 className="evidence-card-title">{event.title}</h3>
					<p className="evidence-card-summary">{event.summary}</p>
				</div>
			</header>
			<ul className="evidence-card-list">
				{decorated.map(
					({
						key,
						ref,
						cellLabel: refCellLabel,
						href,
						beforeAfterVisible,
						beforeText,
						afterText,
					}) => {
						const headerInner = (
							<>
								<span className="evidence-card-filename">{ref.filename}</span>
								<span className="evidence-card-cell-ref">{refCellLabel}</span>
							</>
						);
						return (
							<li key={key} className="evidence-card-row">
								{href ? (
									<a
										href={href}
										onClick={handleOpen(href)}
										className="evidence-card-row-link"
									>
										{headerInner}
									</a>
								) : (
									<div className="evidence-card-row-static">{headerInner}</div>
								)}
								{ref.formula && (
									<div className="evidence-card-formula">{ref.formula}</div>
								)}
								{beforeAfterVisible && (
									<div className="evidence-card-before-after">
										<span>
											before:{" "}
											<span className="evidence-card-before-after-value">
												{beforeText}
											</span>
										</span>
										<span>
											after:{" "}
											<span className="evidence-card-before-after-value">
												{afterText}
											</span>
										</span>
									</div>
								)}
							</li>
						);
					},
				)}
			</ul>
			{footerVisible && (
				<div className="evidence-card-footer">
					{recalcStatus && <span>recalc: {recalcStatus}</span>}
					{warningCount > 0 && (
						<span>
							{warningCount} warning{warningCount === 1 ? "" : "s"}
						</span>
					)}
				</div>
			)}
		</article>
	);
};

export default React.memo(ExcelChangeCard);
